<template>
  <v-navigation-drawer
    class="fill-height"
    fixed
    overlay-color="blue-grey darken-3"
    style="z-index: 99"
    temporary
    width="300"
    right
    :value="show"
    @input="checkDrawerValue"
  >
    <template v-slot:prepend>
      <p class="ma-0 pa-0 pa-4 subtitle2 font-bold">
        {{ selectorTitle }} {{ ` (${selectedColumns.length})` }}
      </p>
    </template>
    <v-divider />
    <v-list dense class="ma-0 pa-0">
      <template v-for="column in columns">
        <v-list-item
          v-if="!column.subColumns"
          class="mt-2 mx-4 pa-0"
          dense
          :key="column.id"
        >
          <v-list-item-action class="ma-0 pa-0">
            <v-checkbox
              class="ma-0 pa-0 pe-4"
              color="blue"
              hide-details
              :ripple="false"
              :value="checkInputValue(column.id)"
              :input-value="checkInputValue(column.id)"
              @change="handleChange(column.id)"
            />
          </v-list-item-action>
          <v-list-item-content class="ma-0 pa-0">
            <v-list-item-title
              class="ma-0 pa-0 font-weight-500 font-size-13 blue-grey--text text--darken-3"
              >{{ column.title }}</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>

        <v-list-group
          v-else
          class="ma-0 pa-0 mt-2"
          :key="`${column.id}_group`"
          :ripple="false"
        >
          <template v-slot:activator>
            <v-list-item-action class="ma-0 pa-0">
              <v-checkbox
                class="ma-0 pa-0 pe-4"
                color="blue"
                hide-details
                :ripple="false"
                :indeterminate="indeterminatePhases"
                :value="checkInputValue(column.id)"
                :input-value="checkInputValue(column.id)"
                @change="handleGroupChange(column.id)"
              />
            </v-list-item-action>

            <v-list-item-content class="ma-0 pa-0">
              <v-list-item-title
                class="ma-0 pa-0 font-weight-500 font-size-13 blue-grey--text text--darken-3"
                >{{ column.title
                }}{{ ` (${column.subColumns.length})` }}</v-list-item-title
              >
            </v-list-item-content>
          </template>

          <v-list-item
            dense
            class="mt-2 mx-4 pa-0"
            v-for="subGroup in column.subColumns"
            :key="subGroup.id"
          >
            <v-list-group
              sub-group
              v-if="subGroup.subColumns"
              class="ma-0 pa-0"
              :key="`${column.id}_group_second`"
              :ripple="false"
            >
              <template v-slot:activator>
                <v-list-item-action class="ma-0 pa-0">
                  <v-checkbox
                    color="blue"
                    class="ma-0 pa-0 pe-4 me-4"
                    hide-details
                    :ripple="false"
                    :value="checkInputValue(subGroup.id)"
                    :input-value="checkInputValue(subGroup.id)"
                    @change="handleSubGroupChange(subGroup.id)"
                  >
                  </v-checkbox>
                </v-list-item-action>

                <v-list-item-content class="ma-0 pa-0">
                  <v-list-item-title
                    class="ma-0 pa-0 font-weight-500 font-size-13 blue-grey--text text--darken-3"
                  >
                    {{ subGroup.title }}{{ ` (${subGroup.subColumns.length})` }}
                  </v-list-item-title>
                </v-list-item-content>
              </template>

              <v-list-item
                dense
                class="mt-2 mx-4 pa-0 ps-10"
                v-for="subGroupSubColumn in subGroup.subColumns"
                :key="subGroupSubColumn.id"
              >
                <v-list-item-title
                  class="ma-0 pa-0 font-weight-500 font-size-13 blue-grey--text text--darken-1"
                >
                  {{ subGroupSubColumn.title }}
                </v-list-item-title>
              </v-list-item>
            </v-list-group>

            <v-list-item-title
              v-if="!subGroup.subColumns"
              class="ma-0 pa-0 ps-11 font-weight-500 font-size-13 blue-grey--text text--darken-1"
            >
              {{ subGroup.title }}
            </v-list-item-title>
          </v-list-item>
        </v-list-group>
      </template>
    </v-list>
    <template v-slot:append>
      <v-divider />
      <div class="px-4 py-6">
        <Button
          block
          color="primary"
          :text="$t('save')"
          @click="$emit('save')"
        />
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import _ from 'lodash'
import Button from '@/ui/shared/components/Button.vue'
import constants from './constants'

export default {
  name: 'ColumnSelector',

  components: { Button },

  props: {
    entityType: {
      type: String,
      required: false,
    },

    selectedColumns: {
      type: Array,
      required: false,
      default: () => [],
    },
    show: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      availableColumnsByEntity: {
        improvementIdea: constants.getImprovementIdeaColumns(),
        quickAction: constants.getQuickActionColumns(),
        problemSolving: constants.getProblemSolvingColumns(),
        routine: constants.getRoutineColumns(),
        finding: constants.getFindingColumns(),
      },
      indeterminatePhases: false,
    }
  },

  computed: {
    isRevEntity() {
      return (
        this.entityType === 'improvementIdea' ||
        this.entityType === 'problemSolving' ||
        this.entityType === 'quickAction'
      )
    },

    columns() {
      return this.availableColumnsByEntity?.[this.entityType] ?? []
    },

    selectorTitle() {
      return this.$t(`common.entity_type.${_.snakeCase(this.entityType)}`)
    },
  },

  methods: {
    checkIndeterminatePhases() {
      this.columns
      const phases = this.columns?.find?.(col => col?.id === 'phases')
      const subColumns = phases?.subColumns?.map?.(col => col?.id)

      return !subColumns?.every?.(col => this.selectedColumns?.includes?.(col))
    },

    handleChange(id) {
      this.$emit('change', id)
    },

    handleSubGroupChange(id) {
      this.handleChange(id)
      this.indeterminatePhases = this.checkIndeterminatePhases()
    },

    handleGroupChange(id) {
      this.handleChange(id)
      const phases = this.columns?.find?.(col => col?.id === 'phases')
      const subColumns = phases?.subColumns?.map?.(col => col?.id) ?? []

      const phasesIsSelected = this.selectedColumns.includes(id)

      if (phasesIsSelected) {
        subColumns?.forEach?.(col => {
          if (this.selectedColumns.includes(col)) return
          this.handleChange(col)
        })
      } else {
        subColumns?.forEach?.(col => {
          if (this.selectedColumns?.includes?.(col)) this.handleChange(col)
          return
        })
      }
    },

    checkInputValue(id) {
      return this.selectedColumns.includes(id)
    },

    checkDrawerValue(val) {
      if (!val) this.$emit('close')
    },
  },
}
</script>
